import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMiscStore } from './misc'
import { useMatterStore } from './matter'
import { fb } from '@/lib/api/firebase'
import { API } from '@/lib/api'
import { signInWithEmailAndPassword, onAuthStateChanged, getIdTokenResult, signOut, sendPasswordResetEmail, signInWithCustomToken, multiFactor, verifyPasswordResetCode, confirmPasswordReset, checkActionCode, applyActionCode } from 'firebase/auth'

import { AuthStep } from '@/lib/enums'

export const useUserStore = defineStore('user', {
	state: () => ({
		me: null,
		claims: {},
		authData: {},
		users: [],
		user: {},
		token: null,
		_loading: 0,
		error: null,
		checkoutAgreement: false,
	}),

	actions: {
		async _setLoading(status, global = true) {
			if (status) {
				this.error = null
				this._loading = Math.max(this._loading + 1, 0)
			} else {
				this._loading = Math.max(this._loading - 1, 0)
			}

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setLoading(status)
			}
		},

		async _setError(error = null, global = true) {
			this.error = error

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setError(error)
			}
		},

		checkUser() {
			return new Promise((resolve, reject) => {
				onAuthStateChanged(fb.auth, async (user) => {
					const { currentUser } = fb.auth
console.log('[checkUser]', {user, currentUser})
					this.me = user
					if (user) {
console.log('[checkUser]', { currentUser,}, )
						try {
							const tokenData = await getIdTokenResult(user)
console.log('[checkUser]', { tokenData, })
							const { claims } = tokenData
							this.claims = claims
console.log('[checkUser]', { claims, })
							this.token = tokenData
						} catch (error) {
							reject(error)
						}
						resolve(user)
					} else {
						reject(`No user`)
					}
				}, reject)
			})
		},

		async loginRemote(fields) {
			const { email, password } = fields
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/authenticate`, { email, password }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = {}
						}

						const { user } = auth
						resolve(user)
					}
				} catch (error) {
					// this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async authVerify(fields) {
			this._setLoading(true)

			const { token, ...data } = fields

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/verify`, data)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = null
						}

						const { user } = auth
						resolve(user)
					}
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async authAgreement(fields) {
			this._setLoading(true)

			const { version } = fields

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/agree`, { version })
					await this.checkUser()
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = null
						}
						const { user } = auth
						resolve(user)
					}
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})

		},

		async authSelect(fields) {
			this._setLoading(true)

			const { token, ...data } = fields

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/select`, data)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = null
						}

						const { user } = auth
						resolve(user)
					}
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async signInCustom(token, tenantId) {
			const auth = await signInWithCustomToken(fb.auth, token)
			await this.checkUser()
			const { user } = auth
			return user
		},

		async registerInvite(fields) {
			const { code, email, firstName, lastName, password, contact, } = fields
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const res = await API.post(`/users/invite`, { code, email, firstName, lastName, password, contact, }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = null
						}
						const { user } = auth
						resolve(user)
					}
				} catch (error) {
					reject(error)
					// this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async register(fields) {
			const { code, email, firstName, lastName, password, uid, contact, } = fields
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/register`, { email, firstName, lastName, password, uid, contact }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = null
						}

						const { user } = auth
						resolve(user)
					}
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async reauth(customerId) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const res = await API.post(`/users/reauth`, { customerId })
					if (res.token) {
						// fb.auth.tenantId = res.tenantId
						// fb.auth.tenantId = 'SENT-PRO-s04qa'
						const auth = await signInWithCustomToken(fb.auth, res.token)
						const { user } = auth
						this.me = user

						resolve(user)
					} else {
						resolve(res)
					}
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getProducts() {

			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.get(`/users/products`, null, {}, false)
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getProduct(productId) {

			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.get(`/users/product/${productId}`, null, {}, false)
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async signOut() {
			this._setLoading(true)

			try {
				await signOut(fb.auth)
				this.me = null
				this.claims = {}
				this.authData = {}
				this.token = null
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async resetPassword(fields) {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			const { email } = fields
			return new Promise(async (resolve, reject) => {
				try {
					await sendPasswordResetEmail(fb.auth, email)
					resolve(email)
				} catch (error) {
					reject(error)
					miscStore.setError(error)
				} finally {
					miscStore.setLoading(false)
				}

			})
		},

		async getUser(userId) {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			return new Promise(async (resolve, reject) => {
				try {
					this.user = await API.get(`/users/${userId}`)
					resolve(this.user)
				} catch (error) {
					miscStore.setError(error)
					reject(error)
				} finally {
					miscStore.setLoading(false)
				}
			})

		},

		async upgradeUser(uid) {
			const miscStore = useMiscStore()
			miscStore.setLoading(true)
			miscStore.setError(null)

			let docsPath = [`users`].join(`/`)

			try {
				await API.post(`/users/${uid}/upgrade`)
				// this.users = await fb.getDocs(docsPath)
			} catch (error) {
				miscStore.setError(error)
			} finally {
				miscStore.setLoading(false)
			}
		},

		async actionResetPassword(data) {
			const { oobCode, continueUrl, password } = data

			this._setLoading(true)
			return new Promise(async (resolve, reject) => {
				verifyPasswordResetCode(fb.auth, oobCode)
					.then((email) => {
						confirmPasswordReset(fb.auth, oobCode, password)
							.then(async (res) => {
								await this.loginRemote({email, password})
								resolve(res)
							})
							.catch(error => {
								this._setError(error.message || error)
								reject(error)
							})
							.finally(() => {
								this._setLoading(false)
							})
					})
					.catch(error => {
						reject(error)
						this._setError(error.message || error)
					})
					.finally(() => {
						this._setLoading(false)
					})
			})
		},

		async actionVerifyEmail(data) {
			const { oobCode, continueUrl, } = data

			this._setLoading(true)
			return new Promise(async (resolve, reject) => {
				applyActionCode(fb.auth, oobCode)
					.then(res => {
						resolve(rest)
					})
					.catch(reject)
					.finally(() => {
						this._setLoading(false)
					})
			})
		},

		async requestLoginLink(data) {
			const { email, token } = data

			this._setLoading(true)
			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/demoSend`, { email, token }, {}, false)
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async actionLoginEmail(data) {
			const { oobCode, email, apiKey } = data

			this._setLoading(true)
			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/demoVerify`, { oobCode, email, apiKey }, {}, false)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res.data
						} else {
							this.authData = {}
						}

						const { user } = auth
						resolve(user)
					}
					// resolve(res)
					console.log('[store.resolve]')
				} catch (error) {
					console.log('[store.catch]', error)
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		unsetUser() {
			this.user = {}
		},

		unsetUsers() {
			this.users = []
		},

		async checkout(uid, params) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const session = await API.post(`/users/checkout`, params, {})
					console.log({ session,})
					resolve(session)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async checkoutComplete(sessionId) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.get(`/users/checkout/${sessionId}`)
					if (res.token) {
						const auth = await signInWithCustomToken(fb.auth, res.token)
						await this.checkUser()

						if (res.incomplete) {
							this.authData = res
						} else {
							this.authData = null
						}

console.log('[authSelect]', { auth, })
						const { user } = auth
						resolve(user)
					}
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async sendSupport(data) {
			this._setLoading(true)

			const { message, topic, } = data
			return new Promise(async (resolve, reject) => {
				try {
					const res = await API.post(`/users/support`, { message, topic, })
					resolve(res)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		}
	},

	getters: {
		userLevel: (state) => state.token?.claims?.metadata?.userLevel,
		isLoading: state => state._loading > 0,
	},
})

